import Dexie from 'dexie';
import type { LeitourDatabase } from '../database/db';
import { databaseManager, type ActiveDatabaseManager } from '../database/databaseManager';
import { syncBuildEnabled, syncTargetProtocolVersion } from '../config/sync';
import { createSyncBatch, type SyncMutationDraftWithBase } from './batch';
import { hashSyncPayloadForEntity, hashSyncPayloadForVersion, payloadVersionForSyncPayload, validateSyncPayloadForVersion } from './canonical';
import { areSyncEntitiesRelated, isSyncEntityRelatedToConflict } from './conflictRelations';
import { syncExclusiveLock, type SyncExclusiveLock, nextRetryAt } from './coordination';
import { applyRemoteEntity, createRemoteConflict, getLocalSyncPayload, listLocalSyncEntities, preflightRemoteEntity, syncTransactionTables } from './localReplica';
import { compareOutboxQueueOrder, createInitialSyncMetadata, nextOutboxQueueOrder } from './localOutbox';
import { recoverLegacyUserPreferenceArtifacts } from './legacyPreferenceRecovery';
import { getOrCreateDeviceId } from './deviceId';
import { syncTelemetry, type SyncTelemetrySink } from './telemetry';
import { coverSyncService, type CoverSyncService } from './coverSync';
import { syncTransport, SyncTransportError, type SyncTransport } from './transport';
import type {
  SyncBatchResult,
  SyncChange,
  SyncConflict,
  SyncEntityState,
  SyncMetadata,
  SyncOutboxRecord,
  SyncPullResult,
  SyncProtocolUpgradeStage,
  SyncProtocolUpgradeState,
  SyncProtocolVersion,
  SyncRuntimeStatus,
  SyncSnapshot,
  SyncSnapshotEntity
} from './types';
import {
  SYNC_CANONICALIZATION_V2,
  SYNC_PAYLOAD_VERSION_V1,
  SYNC_PROTOCOL_V1,
  SYNC_PROTOCOL_V2
} from './types';

export class SyncIntegrityError extends Error {
  readonly code = 'payload_hash_mismatch';
  constructor() {
    super('A integridade dos dados remotos não pôde ser confirmada. Nada foi aplicado.');
    this.name = 'SyncIntegrityError';
  }
}

export class UnsupportedSyncPayloadVersionError extends Error {
  readonly code = 'unsupported_payload_version';
  constructor(readonly payloadVersion: number) {
    super(`O cliente ainda não possui adaptador de domínio para payloadVersion ${payloadVersion}.`);
    this.name = 'UnsupportedSyncPayloadVersionError';
  }
}

export class AwaitingBootstrapAfterCompleteError extends Error {
  readonly code = 'awaiting_bootstrap_after_complete';
  constructor(reason: string) {
    super(`A outbox aguardava bootstrap depois de ele estar completo, mas a recuperação automática não era segura: ${reason}`);
    this.name = 'AwaitingBootstrapAfterCompleteError';
  }
}

type PredictedSyncEntity = { revision: string; hash?: string; deleted: boolean };

const syncEntityKey = (entityType: string, entityId: string): string => `${entityType}:${entityId}`;

const predictedSyncEntities = (states: readonly SyncEntityState[]): Map<string, PredictedSyncEntity> =>
  new Map(states.map((state) => [syncEntityKey(state.entityType, state.entityId), {
    revision: state.remoteRevision,
    hash: state.remotePayloadHash,
    deleted: Boolean(state.deletedAt)
  }]));

const rebaseRecordMutations = (
  record: SyncOutboxRecord,
  predicted: Map<string, PredictedSyncEntity>,
  allowDeleteWithoutRemote: boolean
): SyncMutationDraftWithBase[] => {
  const drafts: SyncMutationDraftWithBase[] = [];
  for (const mutation of record.batch.mutations) {
    const key = syncEntityKey(mutation.entityType, mutation.entityId);
    const remote = predicted.get(key);
    const identical = remote
      ? ((mutation.operation === 'delete' && remote.deleted)
        || (mutation.operation !== 'delete' && !remote.deleted && remote.hash === mutation.payloadHash))
      : false;
    if (identical) continue;
    if (remote?.deleted && mutation.operation !== 'delete') {
      if (!allowDeleteWithoutRemote) {
        throw new AwaitingBootstrapAfterCompleteError(
          `${key} tentaria ressuscitar um tombstone remoto sem protocolo de restore.`
        );
      }
      throw new Error('Uma mutation local tenta ressuscitar um tombstone remoto sem protocolo de restore.');
    }
    if (!remote && mutation.operation === 'delete' && !allowDeleteWithoutRemote) {
      throw new AwaitingBootstrapAfterCompleteError(
        `${key} é um delete sem estado remoto conhecido; base zero não pode ser presumida.`
      );
    }
    if (!remote && mutation.operation === 'restore' && !allowDeleteWithoutRemote) {
      throw new AwaitingBootstrapAfterCompleteError(
        `${key} é um restore sem tombstone remoto conhecido.`
      );
    }
    drafts.push({
      entityType: mutation.entityType,
      entityId: mutation.entityId,
      operation: mutation.operation === 'delete'
        ? 'delete'
        : remote && !remote.deleted
          ? 'update'
          : 'create',
      payload: mutation.payload,
      baseRevision: { state: 'known', revision: remote?.revision ?? '0' }
    });
    const revision = (BigInt(remote?.revision ?? '0') + 1n).toString();
    predicted.set(key, {
      revision,
      hash: mutation.payloadHash,
      deleted: mutation.operation === 'delete'
    });
  }
  return drafts;
};

const advancePredictedWithPersistedRecord = (
  record: SyncOutboxRecord,
  predicted: Map<string, PredictedSyncEntity>
): void => {
  for (const mutation of record.batch.mutations) {
    const key = syncEntityKey(mutation.entityType, mutation.entityId);
    if (mutation.baseRevision.state !== 'known') {
      throw new AwaitingBootstrapAfterCompleteError(`${key} está ativo com base ainda não resolvida.`);
    }
    const current = predicted.get(key);
    const predictedRevision = (BigInt(mutation.baseRevision.revision) + 1n).toString();
    const alreadyRepresented = current
      && current.revision === predictedRevision
      && current.hash === mutation.payloadHash
      && current.deleted === (mutation.operation === 'delete');
    if (alreadyRepresented) continue;
    if (current && current.revision !== mutation.baseRevision.revision) {
      throw new AwaitingBootstrapAfterCompleteError(
        `${key} perdeu a causalidade FIFO local (esperada ${current.revision}, encontrada ${mutation.baseRevision.revision}).`
      );
    }
    if (!current && mutation.baseRevision.revision !== '0') {
      throw new AwaitingBootstrapAfterCompleteError(
        `${key} referencia a revisão ${mutation.baseRevision.revision}, ausente após o pull estável.`
      );
    }
    predicted.set(key, {
      revision: predictedRevision,
      hash: mutation.payloadHash,
      deleted: mutation.operation === 'delete'
    });
  }
};

const rowDeletedAt = (row: SyncSnapshotEntity | SyncChange): string | undefined =>
  row.operation === 'delete'
    ? ('deletedAt' in row && row.deletedAt ? row.deletedAt : 'changedAt' in row ? row.changedAt : new Date().toISOString())
    : undefined;

const stateFrom = (
  row: SyncSnapshotEntity | SyncChange,
  lastChangeId: string
): SyncEntityState => {
  const deletedAt = rowDeletedAt(row);
  return {
    entityType: row.entityType,
    entityId: row.entityId,
    remoteRevision: row.revision,
    remotePayloadHash: row.payloadHash,
    lastChangeId,
    ...(deletedAt ? { deletedAt } : {})
  };
};

const validateRows = async (rows: readonly (SyncSnapshotEntity | SyncChange)[]): Promise<void> => {
  for (const row of rows) {
    if (await hashSyncPayloadForVersion(row.entityType, row.payload, row.payloadVersion) !== row.payloadHash) {
      throw new SyncIntegrityError();
    }
    validateSyncPayloadForVersion(row.entityType, row.payload, row.payloadVersion);
  }
};

const technicalCode = (error: unknown): string => {
  if (
    error instanceof SyncIntegrityError
    || error instanceof UnsupportedSyncPayloadVersionError
    || error instanceof SyncTransportError
    || error instanceof AwaitingBootstrapAfterCompleteError
  ) return error.code;
  if (error instanceof DOMException && error.name === 'AbortError') return 'aborted';
  return 'unexpected_sync_error';
};

type BootstrapStage = 'preflight' | 'snapshot' | 'pull' | 'finalize';

const bootstrapTechnicalCode = (error: unknown, stage: BootstrapStage): string => {
  const code = technicalCode(error);
  return code === 'unexpected_sync_error' ? `bootstrap_${stage}_failed` : code;
};

const includesEntity = (record: SyncOutboxRecord, entityType: string, entityId: string): boolean =>
  record.status !== 'superseded'
  && record.batch.mutations.some((mutation) => mutation.entityType === entityType && mutation.entityId === entityId);

const includesPendingForChange = (record: SyncOutboxRecord, change: SyncChange): boolean => {
  if (change.entityType !== 'book' || change.operation !== 'delete') {
    return includesEntity(record, change.entityType, change.entityId);
  }
  return record.status !== 'superseded'
    && record.batch.mutations.some((mutation) => areSyncEntitiesRelated(mutation, change));
};

const representedBootstrapRecords = (records: readonly SyncOutboxRecord[]): SyncOutboxRecord[] =>
  records.filter(({ status }) => status !== 'superseded');

const latestBootstrapMutationByEntity = (
  records: readonly SyncOutboxRecord[]
): Map<string, { batchId: string; sequence: number; operation: SyncOutboxRecord['batch']['mutations'][number]['operation'] }> => {
  const latest = new Map<string, { batchId: string; sequence: number; operation: SyncOutboxRecord['batch']['mutations'][number]['operation'] }>();
  for (const record of representedBootstrapRecords(records)) {
    for (const mutation of record.batch.mutations) {
      latest.set(`${mutation.entityType}:${mutation.entityId}`, {
        batchId: record.batchId,
        sequence: mutation.sequence,
        operation: mutation.operation
      });
    }
  }
  return latest;
};

const serverConflictToLocal = (record: SyncOutboxRecord, conflict: SyncBatchResult & { status: 'conflict' }): SyncConflict[] =>
  conflict.conflicts.map((item) => {
    const mutation = record.batch.mutations[item.sourceSequence];
    return {
      id: crypto.randomUUID(), status: 'open', entityType: item.entityType, entityId: item.entityId,
      reason: item.reason, batchId: record.batchId, mutationSequence: item.sourceSequence, origin: 'push',
      baseRevision: mutation?.baseRevision ?? { state: 'known', revision: item.remoteRevision ?? '0' },
      localPayload: mutation?.payload, localPayloadHash: mutation?.payloadHash,
      remoteRevision: item.remoteRevision, remotePayload: item.remotePayload,
      remotePayloadHash: item.remotePayloadHash, remoteDeletedAt: item.remoteDeletedAt,
      remotePayloadVersion: item.remotePayload === undefined
        ? undefined
        : payloadVersionForSyncPayload(item.entityType, item.remotePayload),
      dependencyEntityType: item.dependencyEntityType, dependencyEntityId: item.dependencyEntityId,
      dependentEntityType: item.dependentEntityType, dependentEntityId: item.dependentEntityId,
      conflictingEntityId: item.conflictingEntityId,
      detectedAt: new Date().toISOString()
    };
  });

export interface SyncServiceOptions {
  transport?: SyncTransport | null;
  manager?: ActiveDatabaseManager;
  lock?: SyncExclusiveLock;
  buildEnabled?: boolean;
  telemetry?: SyncTelemetrySink;
  now?: () => Date;
  covers?: CoverSyncService | null;
  targetProtocolVersion?: SyncProtocolVersion;
  onProtocolUpgradeStage?: (stage: SyncProtocolUpgradeStage) => void | Promise<void>;
}

export class SyncService {
  private readonly transport: SyncTransport | null;
  private readonly manager: ActiveDatabaseManager;
  private readonly lock: SyncExclusiveLock;
  private readonly buildEnabled: boolean;
  private readonly telemetry: SyncTelemetrySink;
  private readonly now: () => Date;
  private readonly covers: CoverSyncService | null;
  private readonly targetProtocolVersion: SyncProtocolVersion;
  private readonly onProtocolUpgradeStage?: SyncServiceOptions['onProtocolUpgradeStage'];
  private listeners = new Set<() => void>();

  constructor(options: SyncServiceOptions = {}) {
    this.transport = options.transport === undefined ? syncTransport : options.transport;
    this.manager = options.manager ?? databaseManager;
    this.lock = options.lock ?? syncExclusiveLock;
    this.buildEnabled = options.buildEnabled ?? syncBuildEnabled;
    this.telemetry = options.telemetry ?? syncTelemetry;
    this.now = options.now ?? (() => new Date());
    this.covers = options.covers === undefined
      ? (options.transport === undefined ? coverSyncService : null)
      : options.covers;
    this.targetProtocolVersion = options.targetProtocolVersion ?? syncTargetProtocolVersion;
    this.onProtocolUpgradeStage = options.onProtocolUpgradeStage;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  async getStatus(): Promise<SyncRuntimeStatus> {
    return this.manager.runWithActiveDatabase((db) => this.getStatusForDatabase(db));
  }

  private async getStatusForDatabase(db: LeitourDatabase): Promise<SyncRuntimeStatus> {
    const metadata = await db.syncMetadata.get('sync');
    const openSyncConflicts = await db.syncConflicts.where('status').equals('open').toArray();
    const openConflictCount = openSyncConflicts.length
      + await db.syncCoverConflicts.where('status').equals('open').count();
    const orphanedConflictedOutbox = (await db.syncOutbox.where('status').equals('conflicted').toArray())
      .some((record) => !openSyncConflicts.some((conflict) =>
        conflict.batchId === record.batchId
        || record.batch.mutations.some(({ entityType, entityId }) =>
          entityType === conflict.entityType && entityId === conflict.entityId)));
    const activeOutbox = await db.syncOutbox.filter(({ status }) => status !== 'superseded').toArray();
    const rejectedCount = activeOutbox.filter(({ status }) => status === 'rejected').length;
    const sendingCount = activeOutbox.filter(({ status }) => status === 'sending').length;
    const pendingCount = activeOutbox.filter(({ status }) => status === 'pending').length;
    const awaitingBootstrapCount = activeOutbox.filter(({ status }) => status === 'awaiting_bootstrap').length;
    const hasKnownRemoteDomainState = (await db.syncEntityStates.toArray())
      .some(({ entityType }) => entityType !== 'user_preference');
    const upgradeRunning = metadata?.protocolUpgrade?.status === 'running';
    const technicalStateBlocked = metadata?.bootstrapState === 'conflicted'
      || metadata?.bootstrapState === 'failed'
      || metadata?.bootstrapState === 'running'
      || openConflictCount > 0
      || orphanedConflictedOutbox
      || rejectedCount > 0
      || sendingCount > 0
      || upgradeRunning;
    const effectiveEnabled = this.buildEnabled && metadata?.remoteRollout.status === 'allowed';
    const errorCode = metadata?.lastError
      ?? (orphanedConflictedOutbox ? 'orphaned_conflicted_outbox' : undefined)
      ?? (awaitingBootstrapCount > 0 && metadata?.bootstrapState === 'complete'
        ? 'awaiting_bootstrap_after_complete'
        : undefined);
    let activity: SyncRuntimeStatus['activity'] = 'disabled';
    if (effectiveEnabled) {
      if (typeof navigator !== 'undefined' && !navigator.onLine) activity = 'offline';
      else if (openConflictCount || metadata?.bootstrapState === 'conflicted') activity = 'conflict';
      else if (errorCode || rejectedCount || metadata?.bootstrapState === 'failed') activity = 'error';
      else if (upgradeRunning) activity = 'syncing';
      else if (metadata?.bootstrapState === 'not_started' || metadata?.bootstrapState === 'running') activity = 'bootstrapping';
      else if (sendingCount) activity = 'syncing';
      else if (awaitingBootstrapCount && metadata?.bootstrapState !== 'complete') activity = 'bootstrapping';
      else if (pendingCount) activity = 'pending';
      else if (activeOutbox.length) activity = 'error';
      else activity = 'idle';
    }
    return {
      activity, effectiveEnabled,
      remoteRolloutStatus: metadata?.remoteRollout.status ?? 'not_checked',
      bootstrapState: metadata?.bootstrapState ?? 'not_started',
      lastSuccessfulSyncAt: metadata?.lastSuccessfulSyncAt,
      errorCode,
      openConflictCount,
      canResetTechnicalState: metadata?.remoteRollout.status === 'denied'
        && technicalStateBlocked
        && !hasKnownRemoteDomainState
    };
  }

  async resetTechnicalState(userId: string): Promise<SyncRuntimeStatus> {
    return this.manager.runWithActiveDatabase(async (db) => {
      this.assertActiveUser(db, userId);
      if (!this.buildEnabled || !this.transport) {
        throw new Error('A verificação segura do rollout não está disponível nesta versão.');
      }
      return this.lock.run(userId, async () => {
        const allowed = await this.transport!.isRolloutAllowed();
        if (allowed) {
          throw new Error('Desative o rollout remoto antes de reiniciar a tentativa de sincronização.');
        }
        const metadata = await db.syncMetadata.get('sync')
          ?? createInitialSyncMetadata(userId, getOrCreateDeviceId());
        const hasTechnicalBlock = metadata.bootstrapState === 'conflicted'
          || metadata.bootstrapState === 'failed'
          || metadata.bootstrapState === 'running'
          || await db.syncConflicts.where('status').equals('open').count() > 0
          || await db.syncCoverConflicts.where('status').equals('open').count() > 0
          || await db.syncOutbox.where('status').anyOf('sending', 'conflicted', 'rejected').count() > 0;
        if (!hasTechnicalBlock) {
          throw new Error('Não há estado técnico bloqueado que precise ser reiniciado.');
        }

        const initial = createInitialSyncMetadata(userId, metadata.deviceId || getOrCreateDeviceId());
        await db.transaction('rw', [
          db.syncOutbox, db.syncEntityStates, db.syncConflicts, db.syncMetadata,
          db.syncCoverStates, db.syncCoverConflicts
        ], async () => {
          const hasKnownRemoteDomainState = (await db.syncEntityStates.toArray())
            .some(({ entityType }) => entityType !== 'user_preference');
          if (hasKnownRemoteDomainState) {
            throw new Error(
              'Este estado já possui dados sincronizados. O reinício técnico automático foi bloqueado '
              + 'para preservar alterações e exclusões pendentes.'
            );
          }
          await Promise.all([
            db.syncOutbox.clear(), db.syncEntityStates.clear(), db.syncConflicts.clear(),
            db.syncMetadata.clear(), db.syncCoverStates.clear(), db.syncCoverConflicts.clear()
          ]);
          await db.syncMetadata.add(initial);
        });
        this.notify();
        return this.getStatusForDatabase(db);
      });
    });
  }

  async bootstrap(userId: string): Promise<SyncMetadata> {
    return this.manager.runWithActiveDatabase(async (db) => {
      this.assertActiveUser(db, userId);
      if (!this.buildEnabled || !this.transport) return this.ensureMetadata(db, userId);
      return this.lock.run(userId, async () => {
        const metadata = await this.ensureMetadata(db, userId);
        if (this.needsProtocolUpgrade(metadata)) {
          if (!await this.refreshRemoteRollout(db)) return (await db.syncMetadata.get('sync'))!;
          await recoverLegacyUserPreferenceArtifacts(db, userId, this.now().toISOString());
          return this.upgradeProtocolLocked(userId, db);
        }
        return this.bootstrapLocked(userId, db);
      });
    });
  }

  private async ensureMetadata(db: LeitourDatabase, userId: string): Promise<SyncMetadata> {
    let metadata = await db.syncMetadata.get('sync');
    if (!metadata) {
      const deviceId = (await import('./deviceId')).getOrCreateDeviceId();
      metadata = createInitialSyncMetadata(userId, deviceId);
      await db.syncMetadata.add(metadata);
    }
    return metadata;
  }

  private assertActiveUser(db: LeitourDatabase, userId: string): void {
    if (this.manager.getSnapshot().userId !== userId || this.manager.getActiveDatabase() !== db) {
      throw new Error('O banco ativo não pertence ao usuário da sincronização.');
    }
  }

  private async refreshRemoteRollout(db: LeitourDatabase): Promise<boolean> {
    const allowed = await this.transport!.isRolloutAllowed();
    await db.syncMetadata.update('sync', {
      remoteRollout: { status: allowed ? 'allowed' : 'denied', checkedAt: this.now().toISOString() }
    });
    this.notify();
    return allowed;
  }

  private async bootstrapLocked(
    userId: string,
    db: LeitourDatabase,
    rolloutAlreadyAllowed = false
  ): Promise<SyncMetadata> {
    const started = performance.now();
    await this.ensureMetadata(db, userId);
    if (!rolloutAlreadyAllowed && !await this.refreshRemoteRollout(db)) {
      return (await db.syncMetadata.get('sync'))!;
    }
    await recoverLegacyUserPreferenceArtifacts(db, userId, this.now().toISOString());
    let metadata = (await db.syncMetadata.get('sync'))!;
    if (metadata.bootstrapState === 'conflicted') return metadata;
    if (metadata.lastError === 'ambiguous_legacy_outbox_order') {
      throw new Error(
        'A ordem da outbox local antiga não pôde ser determinada com segurança. '
        + 'Revise as alterações locais antes de iniciar a sincronização.'
      );
    }
    let stage: BootstrapStage = 'preflight';
    try {
      await this.preflightBootstrapSeed(db, metadata);
      this.telemetry.record({ type: 'bootstrap', outcome: 'started', bootstrapState: metadata.bootstrapState });
      stage = 'snapshot';
      await db.syncMetadata.update('sync', {
        remoteRollout: { status: 'allowed', checkedAt: this.now().toISOString() },
        bootstrapState: 'running', lastAttemptAt: this.now().toISOString(), lastError: undefined
      });
      this.notify();

      const snapshot = await this.transport!.getSnapshot(metadata.protocolVersion);
      await validateRows(snapshot.entities);
      await this.applyBootstrapSnapshot(db, snapshot);

      stage = 'pull';
      let pull = await this.transport!.pull(snapshot.highWaterChangeId, metadata.protocolVersion);
      while (true) {
        await this.applyPull(db, pull, 'bootstrap');
        if (!pull.hasMore) break;
        pull = await this.transport!.pull(pull.nextCursor, metadata.protocolVersion);
      }

      stage = 'finalize';
      metadata = await this.finalizeBootstrap(db, userId);
      this.telemetry.record({
        type: 'bootstrap', outcome: metadata.bootstrapState === 'conflicted' ? 'conflict' : 'success',
        durationMs: Math.round(performance.now() - started), bootstrapState: metadata.bootstrapState
      });
      this.notify();
      return metadata;
    } catch (error) {
      const code = bootstrapTechnicalCode(error, stage);
      await db.syncMetadata.update('sync', {
        bootstrapState: 'failed', lastError: code, lastAttemptAt: this.now().toISOString()
      });
      this.telemetry.record({ type: 'bootstrap', outcome: 'error', errorCode: code, durationMs: Math.round(performance.now() - started) });
      this.notify();
      throw error;
    }
  }

  private async preflightBootstrapSeed(db: LeitourDatabase, metadata: SyncMetadata): Promise<void> {
    const represented = new Set<string>();
    for (const record of await db.syncOutbox.toArray()) {
      if (record.status === 'superseded') continue;
      for (const mutation of record.batch.mutations) represented.add(`${mutation.entityType}:${mutation.entityId}`);
    }
    for (const state of await db.syncEntityStates.toArray()) {
      represented.add(`${state.entityType}:${state.entityId}`);
    }
    const drafts: SyncMutationDraftWithBase[] = (await listLocalSyncEntities(db))
      .filter(({ entityType, entityId }) => !represented.has(`${entityType}:${entityId}`))
      .map(({ entityType, entityId, payload }) => ({
        entityType, entityId, payload, operation: 'create',
        baseRevision: { state: 'known', revision: '0' }
      } as SyncMutationDraftWithBase));
    if (drafts.length) {
      try {
        await createSyncBatch({
          deviceId: metadata.deviceId,
          mutations: drafts,
          protocolVersion: metadata.protocolVersion
        });
      } catch (error) {
        throw new Error(
          'A biblioteca local excede o limite seguro de um batch inicial. Divisão causal do bootstrap ainda não está disponível.',
          { cause: error }
        );
      }
    }
  }

  private async applyBootstrapSnapshot(db: LeitourDatabase, snapshot: SyncSnapshot): Promise<void> {
    await db.transaction('rw', syncTransactionTables(db), async () => {
      const local = await listLocalSyncEntities(db);
      const localByKey = new Map(local.map((item) => [`${item.entityType}:${item.entityId}`, item]));
      const outbox = await db.syncOutbox.toArray();
      const latestPending = latestBootstrapMutationByEntity(outbox);
      for (const row of snapshot.entities) {
        const key = `${row.entityType}:${row.entityId}`;
        const current = localByKey.get(key);
        const pending = outbox.some((record) => includesEntity(record, row.entityType, row.entityId));
        const pendingDelete = latestPending.get(key)?.operation === 'delete';
        let conflict: SyncConflict | undefined;
        if (current && (row.operation === 'delete' || current.payloadHash !== row.payloadHash)) {
          conflict = await Dexie.waitFor(applyRemoteEntity(db, row, {
            preserveLocal: true,
            conflictReason: row.operation === 'delete' ? 'remote_deleted' : 'bootstrap_divergence'
          }));
        } else if (!current && row.operation !== 'delete' && !pendingDelete) {
          conflict = await applyRemoteEntity(db, row, { preserveLocal: false });
        }
        if (conflict) await db.syncConflicts.add({ ...conflict, origin: 'bootstrap' });
        if (pending && conflict) {
          for (const record of outbox.filter((item) => includesEntity(item, row.entityType, row.entityId))) {
            await db.syncOutbox.update(record.batchId, { status: 'conflicted', lastError: conflict.reason });
          }
        }
        await db.syncEntityStates.put(stateFrom(row, snapshot.highWaterChangeId));
      }
      await db.syncMetadata.update('sync', {
        cursor: { lastChangeId: snapshot.highWaterChangeId },
        bootstrapHighWaterChangeId: snapshot.highWaterChangeId
      });
    });
  }

  private async applyPull(db: LeitourDatabase, pull: SyncPullResult, origin: 'bootstrap' | 'pull' = 'pull'): Promise<void> {
    await validateRows(pull.changes);
    await db.transaction('rw', syncTransactionTables(db), async () => {
      const outbox = await db.syncOutbox.toArray();
      const batches = new Map<string, SyncChange[]>();
      for (const change of pull.changes) {
        const changes = batches.get(change.batchId) ?? [];
        changes.push(change);
        batches.set(change.batchId, changes);
      }
      for (const [batchId, changes] of batches) {
        const plans: Array<{
          change: SyncChange;
          hasPending: boolean;
          pendingRecords: SyncOutboxRecord[];
          conflict?: SyncConflict;
        }> = [];
        for (const change of changes) {
          const previous = await db.syncEntityStates.get([change.entityType, change.entityId]);
          if (previous && BigInt(previous.remoteRevision) >= BigInt(change.revision)) continue;
          const localPayload = await Dexie.waitFor(getLocalSyncPayload(db, change.entityType, change.entityId));
          const localHash = localPayload
            ? await Dexie.waitFor(hashSyncPayloadForEntity(change.entityType, localPayload))
            : undefined;
          const pendingRecords = outbox.filter((record) => includesPendingForChange(record, change));
          const hasPending = pendingRecords.length > 0;
          const localDiverged = previous
            ? localHash !== undefined && localHash !== previous.remotePayloadHash
            : localHash !== undefined;
          const conflict = await Dexie.waitFor(preflightRemoteEntity(db, change, {
            preserveLocal: hasPending || localDiverged,
            conflictReason: change.operation === 'delete' ? 'remote_deleted' : 'revision_mismatch'
          }));
          plans.push({ change, hasPending, pendingRecords, ...(conflict ? { conflict } : {}) });
        }
        const batchHasConflict = plans.some(({ conflict }) => conflict);
        if (batchHasConflict) {
          for (const plan of plans) {
            const conflict = plan.conflict ?? await Dexie.waitFor((async () => {
              const localPayload = await getLocalSyncPayload(db, plan.change.entityType, plan.change.entityId);
              const localHash = localPayload
                ? await hashSyncPayloadForEntity(plan.change.entityType, localPayload)
                : undefined;
              return createRemoteConflict(plan.change, 'batch_conflict', localPayload, localHash);
            })());
            await db.syncConflicts.add({ ...conflict, batchId, mutationSequence: plan.change.changeSequence, origin });
            if (plan.hasPending) {
              for (const record of plan.pendingRecords) {
                await db.syncOutbox.update(record.batchId, { status: 'conflicted', lastError: conflict.reason });
              }
            }
            await db.syncEntityStates.put(stateFrom(plan.change, plan.change.changeId));
          }
          continue;
        }
        for (const { change } of plans) {
          const unexpectedConflict = await applyRemoteEntity(db, change, { preserveLocal: false });
          if (unexpectedConflict) throw new Error('O estado local mudou durante a aplicação atômica de um batch remoto.');
          await db.syncEntityStates.put(stateFrom(change, change.changeId));
        }
      }
      await db.syncMetadata.update('sync', {
        cursor: { lastChangeId: pull.nextCursor },
        lastSuccessfulSyncAt: this.now().toISOString(), lastError: undefined, consecutiveFailures: 0
      });
    });
  }

  private async finalizeBootstrap(db: LeitourDatabase, userId: string): Promise<SyncMetadata> {
    await db.transaction('rw', syncTransactionTables(db), async () => {
      const metadata = await db.syncMetadata.get('sync');
      if (!metadata) throw new Error('Metadata de sincronização ausente durante o bootstrap.');
      const records = (await db.syncOutbox.toArray())
        .sort(compareOutboxQueueOrder);
      const represented = new Set<string>();
      for (const record of representedBootstrapRecords(records)) {
        for (const mutation of record.batch.mutations) represented.add(`${mutation.entityType}:${mutation.entityId}`);
      }
      const predicted = predictedSyncEntities(await db.syncEntityStates.toArray());
      for (const record of records) {
        if (record.status === 'conflicted' || record.status === 'superseded') continue;
        const drafts = rebaseRecordMutations(record, predicted, true);
        if (!drafts.length) {
          await db.syncOutbox.update(record.batchId, { status: 'superseded' });
          continue;
        }
        const batch = await Dexie.waitFor(createSyncBatch({
          deviceId: record.batch.deviceId, mutations: drafts, supersedesBatchId: record.batchId,
          createdAt: record.batch.createdAt, protocolVersion: metadata.protocolVersion
        }));
        await db.syncOutbox.update(record.batchId, { status: 'superseded' });
        await db.syncOutbox.add({
          batchId: batch.batchId, userId, queueOrder: record.queueOrder, status: 'pending', batch, attemptCount: 0,
          createdAt: batch.createdAt, nextAttemptAt: batch.createdAt
        });
      }

      const seedDrafts: SyncMutationDraftWithBase[] = [];
      for (const local of await listLocalSyncEntities(db)) {
        const key = `${local.entityType}:${local.entityId}`;
        if (predicted.has(key) || represented.has(key)) continue;
        seedDrafts.push({
          entityType: local.entityType,
          entityId: local.entityId,
          operation: 'create',
          payload: local.payload,
          baseRevision: { state: 'known', revision: '0' }
        });
      }
      if (seedDrafts.length) {
        const batch = await Dexie.waitFor(createSyncBatch({
          deviceId: metadata.deviceId, mutations: seedDrafts,
          protocolVersion: metadata.protocolVersion
        }));
        await db.syncOutbox.add({
          batchId: batch.batchId, userId, queueOrder: await nextOutboxQueueOrder(db), status: 'pending', batch, attemptCount: 0,
          createdAt: batch.createdAt, nextAttemptAt: batch.createdAt
        });
      }
      const openConflicts = await db.syncConflicts.where('status').equals('open').count();
      await db.syncMetadata.update('sync', {
        bootstrapState: openConflicts ? 'conflicted' : 'complete',
        lastSuccessfulSyncAt: this.now().toISOString(), lastError: undefined, consecutiveFailures: 0
      });
    });
    return (await db.syncMetadata.get('sync'))!;
  }

  private needsProtocolUpgrade(metadata: SyncMetadata): boolean {
    return this.targetProtocolVersion === SYNC_PROTOCOL_V2
      && metadata.protocolVersion === SYNC_PROTOCOL_V1;
  }

  private async checkpointProtocolUpgradeStage(stage: SyncProtocolUpgradeStage): Promise<void> {
    await this.onProtocolUpgradeStage?.(stage);
  }

  private async ensureProtocolUpgradeState(
    db: LeitourDatabase,
    metadata: SyncMetadata
  ): Promise<SyncProtocolUpgradeState> {
    if (metadata.protocolUpgrade?.status === 'running') return metadata.protocolUpgrade;
    const startedAt = this.now().toISOString();
    const protocolUpgrade: SyncProtocolUpgradeState = {
      status: 'running',
      fromProtocolVersion: SYNC_PROTOCOL_V1,
      targetProtocolVersion: SYNC_PROTOCOL_V2,
      stage: 'resolve_sending_v1',
      startedAt
    };
    await db.syncMetadata.update('sync', {
      protocolUpgrade,
      lastAttemptAt: startedAt,
      lastError: undefined
    });
    return protocolUpgrade;
  }

  private async settleSendingV1Record(
    db: LeitourDatabase,
    record: SyncOutboxRecord,
    result: SyncBatchResult
  ): Promise<void> {
    if (result.status === 'applied') {
      await db.transaction('rw', [db.syncOutbox, db.syncEntityStates], async () => {
        for (const mutationResult of result.mutations) {
          const mutation = record.batch.mutations[mutationResult.sourceSequence];
          const emitted = result.emittedChanges.find(({ sourceMutationSequence }) =>
            sourceMutationSequence === mutationResult.sourceSequence);
          if (!mutation) continue;
          await db.syncEntityStates.put({
            entityType: mutation.entityType,
            entityId: mutation.entityId,
            remoteRevision: mutationResult.revision,
            remotePayloadHash: mutation.payloadHash,
            lastChangeId: emitted?.changeId ?? result.lastChangeId,
            ...(mutation.operation === 'delete' ? { deletedAt: result.committedAt } : {})
          });
        }
        await db.syncOutbox.delete(record.batchId);
      });
      return;
    }
    if (result.status === 'conflict') {
      const conflicts = serverConflictToLocal(record, result);
      await db.transaction('rw', [db.syncOutbox, db.syncConflicts], async () => {
        await db.syncConflicts.bulkAdd(conflicts);
        await db.syncOutbox.update(record.batchId, {
          status: 'conflicted', lastError: 'remote_conflict'
        });
      });
      return;
    }
    await db.transaction('rw', [db.syncOutbox, db.syncMetadata], async () => {
      await db.syncOutbox.update(record.batchId, {
        status: 'rejected', lastError: result.code
      });
      await db.syncMetadata.update('sync', {
        lastError: result.code,
        lastAttemptAt: this.now().toISOString()
      });
    });
  }

  private async resolveSendingV1ForUpgrade(db: LeitourDatabase): Promise<void> {
    const records = (await db.syncOutbox.where('status').equals('sending').toArray())
      .sort(compareOutboxQueueOrder);
    for (const record of records) {
      if (record.batch.protocolVersion !== SYNC_PROTOCOL_V1) {
        throw new Error('A outbox contém batch Protocol V2 em sending antes da conclusão do upgrade.');
      }
      await db.syncOutbox.update(record.batchId, {
        attemptCount: record.attemptCount + 1,
        lastAttemptAt: this.now().toISOString(),
        lastError: undefined
      });
      try {
        const result = await this.transport!.applyBatch(record.batch);
        await this.settleSendingV1Record(db, record, result);
      } catch (error) {
        await db.syncOutbox.update(record.batchId, {
          status: 'sending',
          lastError: technicalCode(error),
          lastAttemptAt: this.now().toISOString()
        });
        throw error;
      }
    }
    const metadata = await db.syncMetadata.get('sync');
    if (!metadata?.protocolUpgrade || metadata.protocolUpgrade.status !== 'running') return;
    await db.syncMetadata.update('sync', {
      protocolUpgrade: { ...metadata.protocolUpgrade, stage: 'snapshot_v2' }
    });
  }

  private async applyProtocolUpgradeSnapshot(
    db: LeitourDatabase,
    snapshot: SyncSnapshot
  ): Promise<void> {
    await db.transaction('rw', syncTransactionTables(db), async () => {
      const metadata = await db.syncMetadata.get('sync');
      if (!metadata?.protocolUpgrade || metadata.protocolUpgrade.status !== 'running') {
        throw new Error('Checkpoint do upgrade ausente durante o snapshot Protocol V2.');
      }
      const local = await listLocalSyncEntities(db);
      const localByKey = new Map(local.map((item) => [`${item.entityType}:${item.entityId}`, item]));
      const outbox = await db.syncOutbox.toArray();
      const rebasable = outbox.filter(({ status }) =>
        status === 'pending' || status === 'awaiting_bootstrap');
      const latestPending = latestBootstrapMutationByEntity(rebasable);
      const existingConflicts = await db.syncConflicts.where('status').equals('open').toArray();
      const previousStates = new Map((await db.syncEntityStates.toArray())
        .map((state) => [`${state.entityType}:${state.entityId}`, state] as const));

      await db.syncEntityStates.clear();
      for (const row of snapshot.entities) {
        const key = `${row.entityType}:${row.entityId}` as const;
        const current = localByKey.get(key);
        const pendingRecords = rebasable.filter((record) =>
          includesEntity(record, row.entityType, row.entityId));
        const hasPending = pendingRecords.length > 0;
        const pendingDelete = latestPending.get(key)?.operation === 'delete';
        let conflict: SyncConflict | undefined;

        if (current && (row.operation === 'delete' || current.payloadHash !== row.payloadHash)) {
          const localMatchesKnownRemote = current.payloadHash === previousStates.get(key)?.remotePayloadHash;
          if (!hasPending && localMatchesKnownRemote) {
            conflict = await applyRemoteEntity(db, row, { preserveLocal: false });
          } else if (row.operation === 'delete' || !hasPending) {
            conflict = await Dexie.waitFor(applyRemoteEntity(db, row, {
              preserveLocal: true,
              conflictReason: row.operation === 'delete' ? 'remote_deleted' : 'bootstrap_divergence'
            }));
          }
        } else if (!current && row.operation !== 'delete' && !pendingDelete) {
          conflict = await applyRemoteEntity(db, row, { preserveLocal: false });
        }

        if (conflict) {
          const duplicate = existingConflicts.some((item) =>
            item.entityType === conflict!.entityType
            && item.entityId === conflict!.entityId
            && item.remoteRevision === conflict!.remoteRevision
            && item.remotePayloadHash === conflict!.remotePayloadHash);
          if (!duplicate) {
            const stored = { ...conflict, origin: 'bootstrap' as const };
            await db.syncConflicts.add(stored);
            existingConflicts.push(stored);
          }
          for (const record of pendingRecords) {
            await db.syncOutbox.update(record.batchId, {
              status: 'conflicted', lastError: conflict.reason
            });
          }
        }
        await db.syncEntityStates.put(stateFrom(row, snapshot.highWaterChangeId));
      }

      await db.syncMetadata.update('sync', {
        cursor: { lastChangeId: snapshot.highWaterChangeId },
        bootstrapHighWaterChangeId: snapshot.highWaterChangeId,
        protocolUpgrade: {
          ...metadata.protocolUpgrade,
          stage: 'pull_v2',
          snapshotHighWaterChangeId: snapshot.highWaterChangeId,
          lastStableCursor: snapshot.highWaterChangeId
        }
      });
    });
  }

  private async pullProtocolV2UntilStable(db: LeitourDatabase): Promise<void> {
    while (true) {
      const metadata = await db.syncMetadata.get('sync');
      if (!metadata?.protocolUpgrade || metadata.protocolUpgrade.status !== 'running') return;
      const pull = await this.transport!.pull(metadata.cursor.lastChangeId, SYNC_PROTOCOL_V2);
      await this.applyPull(db, pull, 'bootstrap');
      if (pull.hasMore) continue;
      const current = await db.syncMetadata.get('sync');
      if (!current?.protocolUpgrade || current.protocolUpgrade.status !== 'running') return;
      await db.syncMetadata.update('sync', {
        protocolUpgrade: {
          ...current.protocolUpgrade,
          stage: 'rebase_outbox',
          lastStableCursor: pull.nextCursor
        }
      });
      return;
    }
  }

  private async rebaseV1OutboxForProtocolV2(
    db: LeitourDatabase,
    userId: string
  ): Promise<void> {
    await db.transaction('rw', [db.syncOutbox, db.syncEntityStates, db.syncMetadata], async () => {
      const metadata = await db.syncMetadata.get('sync');
      if (!metadata?.protocolUpgrade || metadata.protocolUpgrade.status !== 'running') return;
      const records = (await db.syncOutbox.toArray()).sort(compareOutboxQueueOrder);
      if (records.some((record) =>
        record.status === 'sending' && record.batch.protocolVersion === SYNC_PROTOCOL_V1)) {
        await db.syncMetadata.update('sync', {
          protocolUpgrade: { ...metadata.protocolUpgrade, stage: 'resolve_sending_v1' }
        });
        return;
      }

      const predicted = predictedSyncEntities(await db.syncEntityStates.toArray());
      for (const record of records) {
        if (record.status === 'superseded' || record.status === 'conflicted' || record.status === 'rejected') {
          continue;
        }
        if (record.batch.protocolVersion === SYNC_PROTOCOL_V2) {
          advancePredictedWithPersistedRecord(record, predicted);
          continue;
        }
        if (record.status !== 'pending' && record.status !== 'awaiting_bootstrap') continue;

        const drafts = rebaseRecordMutations(record, predicted, true);
        if (!drafts.length) {
          await db.syncOutbox.update(record.batchId, { status: 'superseded' });
          continue;
        }
        const replacement = await Dexie.waitFor(createSyncBatch({
          deviceId: record.batch.deviceId,
          mutations: drafts,
          protocolVersion: SYNC_PROTOCOL_V2,
          payloadVersion: SYNC_PAYLOAD_VERSION_V1,
          supersedesBatchId: record.batchId,
          createdAt: record.batch.createdAt
        }));
        await db.syncOutbox.add({
          batchId: replacement.batchId,
          userId,
          queueOrder: record.queueOrder,
          status: 'pending',
          batch: replacement,
          attemptCount: 0,
          createdAt: record.createdAt,
          nextAttemptAt: record.createdAt
        });
        await db.syncOutbox.update(record.batchId, { status: 'superseded' });
      }
      await db.syncMetadata.update('sync', {
        protocolUpgrade: { ...metadata.protocolUpgrade, stage: 'finalize' }
      });
    });
  }

  private async finalizeProtocolUpgrade(db: LeitourDatabase): Promise<void> {
    await db.transaction('rw', [db.syncOutbox, db.syncMetadata], async () => {
      const metadata = await db.syncMetadata.get('sync');
      if (!metadata?.protocolUpgrade || metadata.protocolUpgrade.status !== 'running') return;
      const activeV1 = (await db.syncOutbox.toArray()).filter((record) =>
        record.status !== 'superseded'
        && record.status !== 'conflicted'
        && record.status !== 'rejected'
        && record.batch.protocolVersion === SYNC_PROTOCOL_V1);
      if (activeV1.some(({ status }) => status === 'sending')) {
        await db.syncMetadata.update('sync', {
          protocolUpgrade: { ...metadata.protocolUpgrade, stage: 'resolve_sending_v1' }
        });
        return;
      }
      if (activeV1.length) {
        await db.syncMetadata.update('sync', {
          protocolUpgrade: { ...metadata.protocolUpgrade, stage: 'rebase_outbox' }
        });
        return;
      }
      const completedAt = this.now().toISOString();
      await db.syncMetadata.update('sync', {
        protocolVersion: SYNC_PROTOCOL_V2,
        canonicalization: SYNC_CANONICALIZATION_V2,
        protocolUpgrade: {
          ...metadata.protocolUpgrade,
          status: 'complete',
          stage: 'complete',
          completedAt
        },
        lastSuccessfulSyncAt: completedAt,
        lastAttemptAt: completedAt,
        lastError: undefined,
        consecutiveFailures: 0
      });
    });
  }

  private async upgradeProtocolLocked(userId: string, db: LeitourDatabase): Promise<SyncMetadata> {
    let metadata = (await db.syncMetadata.get('sync'))!;
    let upgrade = await this.ensureProtocolUpgradeState(db, metadata);
    try {
      let iterations = 0;
      while (upgrade.status === 'running' && iterations++ < 20) {
        await this.checkpointProtocolUpgradeStage(upgrade.stage);
        switch (upgrade.stage) {
          case 'resolve_sending_v1':
            await this.resolveSendingV1ForUpgrade(db);
            break;
          case 'snapshot_v2': {
            const snapshot = await this.transport!.getSnapshot(SYNC_PROTOCOL_V2);
            await validateRows(snapshot.entities);
            await this.applyProtocolUpgradeSnapshot(db, snapshot);
            break;
          }
          case 'pull_v2':
            await this.pullProtocolV2UntilStable(db);
            break;
          case 'rebase_outbox':
            await this.rebaseV1OutboxForProtocolV2(db, userId);
            break;
          case 'finalize':
            await this.finalizeProtocolUpgrade(db);
            break;
          case 'complete':
            break;
        }
        metadata = (await db.syncMetadata.get('sync'))!;
        upgrade = metadata.protocolUpgrade ?? upgrade;
      }
      if (upgrade.status !== 'complete') throw new Error('O upgrade de protocolo excedeu o limite seguro de retomadas.');
      return metadata;
    } catch (error) {
      metadata = (await db.syncMetadata.get('sync'))!;
      const stage = metadata.protocolUpgrade?.stage ?? upgrade.stage;
      await db.syncMetadata.update('sync', {
        lastError: `protocol_upgrade_${stage}_${technicalCode(error)}`,
        lastAttemptAt: this.now().toISOString(),
        consecutiveFailures: (metadata.consecutiveFailures ?? 0) + 1
      });
      this.notify();
      throw error;
    }
  }

  async synchronize(userId: string): Promise<SyncRuntimeStatus> {
    return this.manager.runWithActiveDatabase(async (db) => {
      this.assertActiveUser(db, userId);
      if (!this.buildEnabled || !this.transport) return this.getStatusForDatabase(db);
      return this.lock.run(userId, async () => {
        await this.ensureMetadata(db, userId);
        if (typeof navigator !== 'undefined' && !navigator.onLine) return this.getStatusForDatabase(db);
        const allowed = await this.refreshRemoteRollout(db);
        if (!allowed) return this.getStatusForDatabase(db);
        await recoverLegacyUserPreferenceArtifacts(db, userId, this.now().toISOString());
        let metadata = (await db.syncMetadata.get('sync'))!;
        if (metadata.protocolVersion > this.targetProtocolVersion) {
          throw new Error('Downgrade automático do protocolo Sync não é suportado.');
        }
        if (metadata.protocolUpgrade?.status === 'running'
          && this.targetProtocolVersion !== SYNC_PROTOCOL_V2) {
          throw new Error('O upgrade Protocol V2 está congelado porque o alvo do aplicativo continua em V1.');
        }
        let upgradedThisRun = false;
        if (this.needsProtocolUpgrade(metadata)) {
          metadata = await this.upgradeProtocolLocked(userId, db);
          upgradedThisRun = true;
        }
        if (metadata.bootstrapState === 'conflicted') return this.getStatusForDatabase(db);
        if (metadata.bootstrapState !== 'complete') {
          metadata = await this.bootstrapLocked(userId, db, true);
          if (metadata.bootstrapState !== 'complete') return this.getStatusForDatabase(db);
        }
        try {
          let iterations = 0;
          while (iterations++ < 100) {
            await this.pullUntilStable(db);
            await this.recoverAwaitingBootstrapAfterComplete(db, userId);
            const record = await this.nextFifoRecord(db);
            if (!record) break;
            if (await this.isBlockedByOpenConflict(db, record)) break;
            const shouldContinue = await this.sendRecord(db, record);
            if (!shouldContinue) break;
          }
          if (this.covers && !upgradedThisRun) await this.covers.reconcile(db, userId);
          this.notify();
          return this.getStatusForDatabase(db);
        } catch (error) {
          const current = await db.syncMetadata.get('sync');
          await db.syncMetadata.update('sync', {
            lastError: technicalCode(error), lastAttemptAt: this.now().toISOString(),
            consecutiveFailures: (current?.consecutiveFailures ?? 0) + 1
          });
          this.notify();
          throw error;
        }
      });
    });
  }

  private async recoverAwaitingBootstrapAfterComplete(
    db: LeitourDatabase,
    userId: string
  ): Promise<void> {
    await db.transaction('rw', [db.syncOutbox, db.syncEntityStates, db.syncMetadata], async () => {
      const metadata = await db.syncMetadata.get('sync');
      if (metadata?.bootstrapState !== 'complete') return;
      const records = (await db.syncOutbox.toArray()).sort(compareOutboxQueueOrder);
      if (!records.some(({ status }) => status === 'awaiting_bootstrap')) return;

      const predicted = predictedSyncEntities(await db.syncEntityStates.toArray());
      for (const record of records) {
        if (record.status === 'superseded') continue;
        if (record.status === 'conflicted' || record.status === 'rejected') break;
        if (record.status !== 'awaiting_bootstrap') {
          advancePredictedWithPersistedRecord(record, predicted);
          continue;
        }

        const drafts = rebaseRecordMutations(record, predicted, false);
        if (!drafts.length) {
          await db.syncOutbox.update(record.batchId, { status: 'superseded' });
          continue;
        }
        const batch = await Dexie.waitFor(createSyncBatch({
          deviceId: record.batch.deviceId,
          mutations: drafts,
          supersedesBatchId: record.batchId,
          createdAt: record.batch.createdAt,
          protocolVersion: metadata.protocolVersion
        }));
        await db.syncOutbox.update(record.batchId, { status: 'superseded' });
        await db.syncOutbox.add({
          batchId: batch.batchId,
          userId,
          queueOrder: record.queueOrder,
          status: 'pending',
          batch,
          attemptCount: 0,
          createdAt: record.createdAt,
          nextAttemptAt: record.createdAt
        });
      }
    });
  }

  private async nextFifoRecord(db: LeitourDatabase): Promise<SyncOutboxRecord | undefined> {
    const records = (await db.syncOutbox.toArray())
      .filter(({ status }) => status !== 'superseded')
      .sort(compareOutboxQueueOrder);
    const first = records[0];
    if (!first) return undefined;
    if (first.status === 'sending') return first;
    if (first.status !== 'pending') return undefined;
    return first.nextAttemptAt <= this.now().toISOString() ? first : undefined;
  }

  private async isBlockedByOpenConflict(db: LeitourDatabase, record: SyncOutboxRecord): Promise<boolean> {
    return (await db.syncConflicts.where('status').equals('open').toArray()).some((conflict) =>
      conflict.batchId === record.batchId
      || record.batch.mutations.some((mutation) => isSyncEntityRelatedToConflict(mutation, conflict)));
  }

  private async pullUntilStable(db: LeitourDatabase): Promise<void> {
    const metadata = await db.syncMetadata.get('sync');
    let cursor = metadata?.cursor.lastChangeId ?? '0';
    const protocolVersion = metadata?.protocolVersion ?? SYNC_PROTOCOL_V1;
    while (true) {
      const started = performance.now();
      const pull = await this.transport!.pull(cursor, protocolVersion);
      await this.applyPull(db, pull);
      this.telemetry.record({ type: 'pull', outcome: 'success', durationMs: Math.round(performance.now() - started), batchCount: new Set(pull.changes.map(({ batchId }) => batchId)).size });
      cursor = pull.nextCursor;
      if (!pull.hasMore) return;
    }
  }

  private async sendRecord(db: LeitourDatabase, record: SyncOutboxRecord): Promise<boolean> {
    const attemptCount = record.attemptCount + 1;
    await db.syncOutbox.update(record.batchId, {
      status: 'sending', attemptCount, lastAttemptAt: this.now().toISOString(), lastError: undefined
    });
    const started = performance.now();
    try {
      const result = await this.transport!.applyBatch(record.batch);
      if (result.status === 'applied') {
        await db.transaction('rw', [db.syncOutbox, db.syncEntityStates], async () => {
          for (const mutationResult of result.mutations) {
            const mutation = record.batch.mutations[mutationResult.sourceSequence];
            const emitted = result.emittedChanges.find(({ sourceMutationSequence }) => sourceMutationSequence === mutationResult.sourceSequence);
            if (!mutation) continue;
            await db.syncEntityStates.put({
              entityType: mutation.entityType, entityId: mutation.entityId,
              remoteRevision: mutationResult.revision, remotePayloadHash: mutation.payloadHash,
              lastChangeId: emitted?.changeId ?? result.lastChangeId,
              ...(mutation.operation === 'delete' ? { deletedAt: result.committedAt } : {})
            });
          }
          await db.syncOutbox.delete(record.batchId);
        });
        this.telemetry.record({ type: 'push', outcome: 'success', mutationCount: record.batch.mutations.length, durationMs: Math.round(performance.now() - started), retryCount: attemptCount - 1 });
        return true;
      }
      if (result.status === 'conflict') {
        const conflicts = serverConflictToLocal(record, result);
        await db.transaction('rw', [db.syncOutbox, db.syncConflicts], async () => {
          await db.syncConflicts.bulkAdd(conflicts);
          await db.syncOutbox.update(record.batchId, { status: 'conflicted', lastError: 'remote_conflict' });
        });
        this.telemetry.record({ type: 'push', outcome: 'conflict', mutationCount: record.batch.mutations.length });
        return false;
      }
      await db.transaction('rw', [db.syncOutbox, db.syncMetadata], async () => {
        await db.syncOutbox.update(record.batchId, { status: 'rejected', lastError: result.code });
        await db.syncMetadata.update('sync', {
          lastError: result.code,
          lastAttemptAt: this.now().toISOString()
        });
      });
      this.telemetry.record({ type: 'push', outcome: 'error', errorCode: result.code, mutationCount: record.batch.mutations.length });
      return false;
    } catch (error) {
      const code = technicalCode(error);
      await db.syncOutbox.update(record.batchId, {
        status: 'pending', lastError: code, nextAttemptAt: nextRetryAt(attemptCount, this.now().getTime())
      });
      this.telemetry.record({ type: 'push', outcome: 'retry', errorCode: code, retryCount: attemptCount });
      if (error instanceof SyncTransportError && !error.retryable) throw error;
      return false;
    }
  }
}

export const syncService = new SyncService();
